// src/components/mitchRoom/SaveBar.js
// Shared fixed-bottom save button + save-related hooks for the Stats Labels /
// Modelling Features pages. Local to the mitchRoom section.
import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';

/**
 * Fixed-bottom centered save button. Stays in place while saving (disabled +
 * spinner beside an unchanged label) so there is no layout shift. Result
 * feedback is the caller's job (sonner toast).
 *
 * Centered over the main content column: the desktop sidebar is 260px wide,
 * so shift by half of that at md+.
 */
export default function SaveBar({ onSave, saving = false, label = 'Save', disabled = false }) {
  return (
    <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 md:left-[calc(50%+130px)]">
      <Button
        onClick={onSave}
        disabled={saving || disabled}
        className="min-w-[200px] shadow-lg"
      >
        {saving && <Loader2 className="animate-spin" aria-hidden="true" />}
        {label}
      </Button>
    </div>
  );
}

/**
 * Cmd/Ctrl+S triggers onSave. Pass a stable handler (it should no-op while a
 * save is already in flight).
 */
export function useSaveShortcut(onSave, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        onSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onSave, enabled]);
}

/** Browser beforeunload warning while there are unsaved changes. */
export function useUnsavedWarning(dirty) {
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}
